"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleDot, Clock, Droplet, Flame, PhoneCall, Search, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const servicesList = [{
  title: "Emergency Plumbing",
  description: "Fast 24/7 dispatched response for active floods, burst pipes, and sudden catastrophic leaks.",
  url: "/emergency-plumbing/",
  badge: "24/7 Rapid Response",
  icon: "emergency"
}, {
  title: "Drain Cleaning",
  description: "High-power snaking and clearing to remove tough clogs, standing water, and recurring backups.",
  url: "/drain-cleaning/",
  badge: "Same-Day Service",
  icon: "drain"
}, {
  title: "Water Heater Repair",
  description: "Prompt diagnosis and repair for no hot water, leaking storage tanks, or faulty heating elements.",
  url: "/water-heater-repair/",
  badge: "Tank & Tankless",
  icon: "waterheater"
}, {
  title: "Leak Detection",
  description: "Non-invasive acoustic and thermal testing to locate hidden moisture behind walls and slabs.",
  url: "/leak-detection-repair/",
  badge: "Pinpoint Accuracy",
  icon: "leak"
}, {
  title: "Toilet Repair",
  description: "Immediate fixes for severe overflows, silent tank leaks, constant running, and blockages.",
  url: "/toilet-repair/",
  badge: "Full Diagnostics",
  icon: "toilet"
}, {
  title: "Sewer Line Service",
  description: "Camera line inspections, tree root clearing, and trenchless underground pipe repairs.",
  url: "/sewer-line-repair/",
  badge: "Camera Inspection",
  icon: "sewer"
}, {
  title: "Pipe Repair",
  description: "Long-term repairs for corroded fittings, cracked lines, frozen breaks, and localized repiping.",
  url: "/pipe-repair/",
  badge: "Durable Materials",
  icon: "pipe"
}, {
  title: "Faucet Repair",
  description: "Quick resolution for dripping faucets, low pressure lines, damaged stems, and valve replacements.",
  url: "/faucet-sink-repair/",
  badge: "Fixture Specialist",
  icon: "faucet"
}];
export default function Services() {
  const prefersReduced = useReducedMotion();
  const renderIcon = (type: string) => {
    switch (type) {
      case "emergency":
        return <ShieldAlert className="size-6 text-destructive" />;
      case "drain":
        return <Droplet className="size-6 text-primary" />;
      case "waterheater":
        return <Flame className="size-6 text-accent-foreground" />;
      case "leak":
        return <Search className="size-6 text-primary" />;
      case "toilet":
        return <CircleDot className="size-6 text-primary" />;
      case "sewer":
        return <AlertTriangle className="size-6 text-accent-foreground" />;
      case "pipe":
        return <Wrench className="size-6 text-primary" />;
      case "faucet":
        return <Wrench className="size-6 text-primary" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="coreservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 font-semibold px-3 py-1">
            <Clock className="size-3.5 mr-1 text-primary" />
            24/7 Jonesboro Plumbing Services
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Fast, Reliable Plumbing Solutions
          </h2>
          <p className="text-muted-foreground text-base md:text-lg font-default leading-relaxed">
            When plumbing emergencies strike your home or business, our licensed Jonesboro technicians arrive equipped
            to solve the issue on the first visit.
          </p>
        </div>

        {/* 8-Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesList.map((service, i) => <motion.div key={service.title} data-index={i} initial={prefersReduced ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={prefersReduced ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.35,
          delay: prefersReduced ? 0 : i * 0.05
        }} className="flex">
              <Card className="flex flex-col justify-between w-full bg-card hover:shadow-md transition-shadow border border-border rounded-xl">
                <div>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-12 rounded-lg bg-secondary flex items-center justify-center">
                        {renderIcon(service.icon)}
                      </div>
                      <Badge variant="outline" className="text-2xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </div>
                <CardFooter className="pt-4 border-t border-border/50 flex items-center justify-between">
                  <Button variant="link" asChild className="p-0 h-auto font-medium text-primary">
                    <Link to={service.url} className="inline-flex items-center gap-1">
                      <span>View details</span>
                      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Urgent Callout Footer Banner */}
        <div className="mt-12 bg-secondary/70 border border-border rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h3 className="text-lg md:text-xl font-default font-semibold text-foreground">
              Facing an active leak or urgent plumbing backup?
            </h3>
            <p className="text-sm text-muted-foreground font-default">
              Our dispatch team is available around the clock in Jonesboro, GA and surrounding areas.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold shadow-sm">
              <Link to="/contact/">
                <PhoneCall className="size-4 mr-1" />
                <span>Call Dispatch Now</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-semibold">
              <Link to="/services/">
                <span>All Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}